import { useMemo, useState } from "react";
import { Logo } from "@/components/Logo";
import { StateMessage } from "@/components/StateMessage";
import { PickerGrid } from "@/components/PickerGrid";
import { AmountKeypad } from "@/components/AmountKeypad";
import { PaymentAnomalyNotice } from "@/components/PaymentAnomalyNotice";
import { BoothReset } from "@/components/BoothReset";
import { ReceiptConfidenceNotice } from "@/components/ReceiptConfidenceNotice";
import { isPlausibleReceiptDate } from "@/lib/receiptDate";
import { ReceiptScan } from "@/components/ReceiptScan";
import { OutboxStatus } from "@/components/OutboxStatus";
import { Icon } from "@/components/Icon";
import { useReferenceData } from "@/hooks/useReferenceData";
import { useOutbox } from "@/hooks/useOutbox";
import { useUsageCounts } from "@/hooks/useUsageCounts";
import { orderByUsage } from "@/lib/usage";
import { ApiError, REQUEST_TIMED_OUT, demoApi } from "@/api/client";
import { outbox } from "@/offline/outbox";
import type { CreateEntryResponse, PaymentMethod, ReceiptAnalysis } from "@/api/types";
import "./QuickEntry.css";

/**
 * Default visible count for the vendor and category pickers before a "show
 * more" expander is needed (fc-collapse).
 *
 * Measured on the production build at 375x812 before this change: 8 vendors
 * + 19 categories rendered flat cost 2.49 screens of scroll, and the Amount
 * step sat 1.85 screens down — most of that from these two lists. Capping
 * each at its ~6 most-used entries (`lib/usage.ts` / `hooks/useUsageCounts.ts`)
 * is the fix; a visitor picking one of those still finishes in the same 8
 * taps as before, and only a less-common pick costs the extra "show more" tap.
 */
const MOST_USED_COUNT = 6;

/**
 * `"queued"` is the offline-outbox path (of-002): the entry was accepted
 * on-device but could not reach the server yet. Deliberately its own state
 * rather than reusing `"success"` — the copy on screen has to say "this
 * phone" instead of "the laptop dashboard", since that hasn't happened yet.
 */
type SubmitState = "idle" | "submitting" | "success" | "queued" | "error";

/**
 * Phone quick-entry — the visitor journey. Must be completable in about
 * 10 seconds (docs/demo/tradeshow-2026-09.md): pick a vendor, pick a
 * category, optionally pick how it was paid, set an amount, submit.
 * Constrained inputs only, no free text — every choice here is a tap,
 * never a keyboard.
 */
export function QuickEntry() {
  const { vendors, accounts, paymentMethods, entityId, status, error, fromCache } =
    useReferenceData();
  /** Entries accepted while offline and not yet replayed — of-002/of-003. */
  const outboxItems = useOutbox();
  /** This tenant's real usage — what orders the vendor/category pickers below. */
  const usage = useUsageCounts();

  const [vendorId, setVendorId] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [amountDigits, setAmountDigits] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<CreateEntryResponse | null>(null);
  /** Set when this entry came from a receipt scan — drives the confirm-mapping write. */
  const [scan, setScan] = useState<ReceiptAnalysis | null>(null);
  /**
   * A booking day the VISITOR chose, overriding whatever the model read.
   *
   * Null means "use the read date", which is every entry until somebody touches the
   * control — so the default behaviour is byte-for-byte what it was. Cleared by both
   * `applyScan` and `resetForm`: a new photograph is a new claim about the date, and a
   * correction that outlived the receipt it corrected would silently backdate the next one.
   */
  const [dateOverride, setDateOverride] = useState<string | null>(null);

  /**
   * Drop an analysis into the existing pickers.
   *
   * Money crosses a boundary here: the API returns dollars as a decimal, but this
   * form tracks CENTS as a digit string and never does float arithmetic. So the
   * conversion is a single rounded multiply at the edge, not arithmetic carried
   * through the component.
   */
  function applyScan(analysis: ReceiptAnalysis): void {
    setScan(analysis);
    setDateOverride(null);
    if (analysis.vendorId) setVendorId(analysis.vendorId);
    if (analysis.accountId) setAccountId(analysis.accountId);
    if (analysis.amount != null && analysis.amount > 0) {
      setAmountDigits(String(Math.round(analysis.amount * 100)));
    }
  }

  const amountCents = amountDigits === "" ? 0 : parseInt(amountDigits, 10);

  /*
    The receipt's own date, derived ONCE — used both to BOOK the entry and to tell the
    reader which day it lands on, so those two can never disagree.

    `isPlausibleReceiptDate` gates it. The server refuses a future or years-old date with a
    400, which is right for a write path and wrong as a booth experience: a visitor's scan
    must not fail because the model misread a year. When the date is not believable the
    entry books today, exactly as every entry did before this.
  */
  const receiptDate = scan && isPlausibleReceiptDate(scan.date) ? scan.date : null;

  /*
    What this entry ACTUALLY books on: the visitor's correction if they made one, otherwise
    the read date, otherwise today. One expression, used both to send `occurredOn` and to
    seed the control — the same reason `receiptDate` above is derived once, so what the
    screen says and what the server stores cannot disagree.
  */
  const bookingDate = dateOverride ?? receiptDate;

  // The payment-method step only exists when the API actually has data for
  // it — GET /api/demo/payment-methods may 404 (not deployed yet) or the
  // tenant may have none seeded. Either way, the step is skipped entirely
  // rather than blocking submission on a picker with nothing to pick from.
  const showPaymentStep = paymentMethods.length > 0;

  const canSubmit =
    // entityId is discovered, not configured — blocked rather than guessed if the
    // tenant somehow has none. See useReferenceData.
    entityId !== null &&
    vendorId !== null &&
    accountId !== null &&
    amountCents > 0 &&
    (!showPaymentStep || paymentMethodId !== null);

  /**
   * Most-used vendors first — real usage, not alphabetical and not a
   * hardcoded guess. See `hooks/useUsageCounts.ts` / `lib/usage.ts`.
   */
  const vendorItems = useMemo(
    () => orderByUsage(vendors.map((v) => ({ id: v.id, label: v.name })), usage.vendors),
    [vendors, usage.vendors],
  );
  /**
   * EXPENSE accounts only.
   *
   * Quick-entry records spending — a receipt out of someone's pocket. Offering the
   * full chart of accounts means a visitor is invited to file a feed-store receipt
   * against "Savings" or "Grain Sales", which is not merely untidy: a farmer reads
   * that list, sees asset and income accounts mixed into a purchase category
   * picker, and concludes the software does not understand accounting. That is the
   * exact credibility we are at the booth to build.
   *
   * It also cuts the list from 24 options to ~19, which matters when the whole
   * interaction is meant to take about ten seconds on a phone.
   *
   * If the flow ever grows to record income too, this becomes a mode rather than a
   * filter — do not just widen it back.
   */
  const accountItems = useMemo(
    () =>
      orderByUsage(
        accounts
          .filter((a) => a.kind === "Expense")
          .map((a) => ({ id: a.id, label: a.name })),
        usage.accounts,
      ),
    [accounts, usage.accounts],
  );
  const paymentItems = useMemo(
    () =>
      paymentMethods.map((m) => ({ id: m.id, label: m.label })),
    [paymentMethods],
  );
  const paymentMethodById = useMemo(
    () => new Map<string, PaymentMethod>(paymentMethods.map((m) => [m.id, m] as const)),
    [paymentMethods],
  );

  /**
   * BOOTH BEAT 2, on the surface the beat is actually performed on.
   *
   * `accountIsConfirmed` means the category was not inferred — it was recalled from a
   * mapping a human confirmed earlier. The spec is explicit that this is the deterministic
   * half of the demo: *"Auto-maps instantly, zero touch. It learned."* / *"Cannot be wrong
   * — a vendor→account lookup, not inference"* (tradeshow-2026-09.md, "Three scans, three
   * escalating beats").
   *
   * IT WAS SAID ONLY ON THE DASHBOARD. `ReceiptCapture` — the laptop widget — renders the
   * "Remembered …" line; QuickEntry never read the flag at all. But the three-receipt run
   * is performed on the TETHERED PHONE (the doc's own table: "Booth Demo | The tethered
   * phone. Christy's scripted 3-receipt + airplane-mode run"), so the second scan looked
   * identical to the first on the one screen a prospect is watching. Found on 2026-09-06 by
   * walking the script in a phone-sized browser rather than through the API.
   *
   * That is the same defect that was already fixed once this session, fixed on the wrong
   * surface — and the API had been returning the signal correctly the whole time.
   */
  const rememberedVendorName = useMemo(() => {
    if (!scan?.accountIsConfirmed || !scan.vendorId) return null;
    return vendors.find((v) => v.id === scan.vendorId)?.name ?? null;
  }, [scan, vendors]);
  const rememberedCategoryName = useMemo(() => {
    if (!scan?.accountIsConfirmed || !scan.accountId) return null;
    return accounts.find((a) => a.id === scan.accountId)?.name ?? null;
  }, [scan, accounts]);

  const amountStepNumber = showPaymentStep ? 4 : 3;

  function resetForm(): void {
    setVendorId(null);
    setAccountId(null);
    setPaymentMethodId(null);
    setAmountDigits("");
    setSubmitState("idle");
    setSubmitError(null);
    setLastResult(null);
    setScan(null);
    setDateOverride(null);
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit || !vendorId || !accountId || !entityId) return;
    setSubmitState("submitting");
    setSubmitError(null);
    const payload = {
      entityId,
      vendorId,
      accountId,
      amount: amountCents / 100,
      ...(paymentMethodId ? { paymentMethodId } : {}),
      // Only a scan can supply this; a hand-typed entry books today as it always has.
      // `bookingDate` is the read date UNLESS the visitor corrected it on the notice above.
      ...(bookingDate ? { occurredOn: bookingDate } : {}),
      // AND SAY WHERE IT CAME FROM. Omitted, the API defaults to Manual — so every receipt
      // Christy scans on the tethered phone landed on the Live Wall labelled "· Manual ·"
      // (EntryRow.tsx: `if (entry.source !== "Receipt") return "Manual"`), directly
      // contradicting the beat she had just performed in front of the prospect.
      //
      // Measured 2026-09-07 by running the whole scripted three-receipt sequence against the
      // live model: three scans, three ledger rows, `source = manual` on all three.
      //
      // ReceiptCapture.tsx — the LAPTOP widget — has always sent it, and says why: "REQUIRED,
      // not decoration — the caller's feed and the uncategorized-receipt count both key on
      // it." The phone is where the scripted run actually happens, and it was the half that
      // did not. Same shape as booth beat 2, which was also correct on the laptop and absent
      // on the phone.
      ...(scan ? { source: "Receipt" as const } : {}),
      // AND THE PHOTOGRAPH THE API ALREADY KEPT.
      //
      // `ReceiptScan.tsx` tells the visitor, in so many words: "The photo is kept with the
      // entry, so you can open the receipt later." It was not. `POST /receipts/analyze`
      // stores the image and returns its id; the LAPTOP widget forwards it (ReceiptCapture,
      // ef-006) and the phone did not, so every scan on the tethered phone left an orphaned
      // image and an entry with no receipt to open.
      //
      // Measured 2026-09-07 on the booth surface: analyze returned
      // `receiptImageId: 39196782-…`, the screen showed that promise, the entry came back
      // `receipt_attached=NO`, and the tenant held 76 unlinked receipt_image rows.
      //
      // Third defect from the same asymmetry — beat 2's UI, the source tag, and now this were
      // each right on the laptop and absent on the phone, which is the surface the scripted
      // run is performed on.
      ...(scan?.receiptImageId ? { receiptImageId: scan.receiptImageId } : {}),
    };
    try {
      const response = await demoApi.createEntry(payload);
      setLastResult(response);
      setSubmitState("success");

      // THIS IS WHAT MAKES THE SECOND SCAN INSTANT.
      //
      // Whatever the visitor actually submitted becomes the confirmed mapping for
      // this vendor — whether they accepted the model's guess or corrected it.
      // That is the design: the first scan is allowed to be wrong because the
      // correction teaches the system, and the next scan of the same vendor is a
      // plain lookup that cannot be wrong.
      //
      // Only for scanned entries. A manual entry is someone picking a category
      // once, not teaching a rule, and silently turning that into a standing
      // mapping would surprise them later.
      //
      // Deliberately not awaited into the success path: the entry is already
      // saved, and a failure to record the teaching must not present as a failed
      // submission. Logged for whoever is running the booth.
      if (scan) {
        void demoApi
          .confirmMapping({ vendorId, accountId })
          .catch((err: unknown) =>
            console.warn("Entry saved, but the vendor mapping was not recorded.", err),
          );
      }
    } catch (err) {
      // OFFLINE OUTBOX (of-002). The test is "did this reach the application at
      // all" — see ApiError.neverReachedTheServer for exactly which statuses
      // qualify and why 500/503 deliberately do not. The task is explicit: the
      // visitor must never see an error for an entry the outbox accepted, so
      // this branches away from the error state entirely rather than showing
      // one and quietly recovering.
      //
      // This used to test `status === 0` alone, which is a dead NETWORK. With the
      // api container stopped and the phone's wifi perfectly healthy, Caddy
      // answers 502, no offline event fires, and the visitor was shown a raw
      // "502 Bad Gateway" — the exact error of-002 says must never appear.
      if (err instanceof ApiError && err.neverReachedTheServer) {
        // Carry the mapping lesson along too (build task #3) — only set
        // when this entry came from an accepted/corrected scan, the same
        // condition the online success path above checks.
        await outbox.enqueue(payload, scan ? { vendorId, accountId } : null);
        setSubmitState("queued");
        return;
      }
      setSubmitState("error");
      /*
        A PERSON reads this, so an ApiError never speaks HTTP here.

        `ApiError.message` is built from the status line — with the database stopped and the
        API still up, submitting showed the visitor "500 Internal Server Error", in a live
        region so a screen reader announced it too. That is protocol text on a farmer's
        phone at a trade show, and it tells them nothing they can act on.

        The distinction preserved from the original: a PLAIN Error is still surfaced
        verbatim, because those are misconfigurations an operator genuinely needs to see —
        "VITE_DEMO_TENANT is not set. Refusing to call the demo API without a tenant" is
        exactly the message that should not be flattened into "try again". Only the HTTP
        ones get a human sentence.

        Deliberately NOT queued. ApiError.neverReachedTheServer covers the statuses that
        prove the request never arrived (0, 502, 504); a 500 means the application ran and
        failed, so the entry may or may not have been recorded and retrying it blindly is
        how you get a duplicate row on the wall.

        A TIMEOUT gets its own sentence for the same reason and one more. With the api
        container PAUSED — a hung back end, which is what a degraded link looks like — this
        button read "Submitting…" and stayed disabled at 2s, 9s and 22s with no error at
        all. A visitor hands the phone back and walks away. Now it ends at 15s and says so,
        and the wording tells them the one thing they need before retrying: the entry may
        already have landed, because a timeout cannot tell us either way.
      */
      setSubmitError(
        err instanceof ApiError && err.status === REQUEST_TIMED_OUT
          ? "That took too long to send. Check the entry did not already land, then try again."
          : err instanceof ApiError
            ? "Could not save that entry — the server had a problem. Try again in a moment."
            : err instanceof Error
              ? err.message
              : "Could not submit — try again.",
      );
    }
  }

  // Small, reachable-but-not-prominent header shared by the ready and
  // success screens — see BoothReset.tsx for the gesture + placement
  // rationale. Not shown on the loading/error screens: those are
  // transient/rare states, not where the operator resets between prospects.
  /*
    THE RESET CONTROL BELONGS ONLY ON THE BOOTH BUNDLE.

    It was rendered unconditionally, which put an operator-only control on the WALL bundle —
    the one visitors load from the posterboard QR. What happens there depends entirely on who
    is holding a session, and I measured both:

      anonymous visitor   POST /api/demo/reset -> 401. Harmless, but they get an error
                          dialog from a control that could never have worked.
      OWNER session       the reset SUCCEEDS. On 2026-09-02 I did the gesture on :8080 in a
                          browser still signed in as wall@ from earlier, and it wiped the
                          Live Wall's 18 seeded entries — reporting "Demo reset — ready for
                          the next visitor." Recovery is the documented DELETE-and-reseed
                          SQL, which is not something anyone does at a booth.

    That second case is the demo risk, because the booth LAPTOP is signed in as the wall
    owner in order to show the dashboard. Christy scanning her own QR on that laptop to
    demonstrate the visitor flow is a completely ordinary thing to do.

    The API is right and unchanged — an owner resetting their own tenant is deliberate
    (DemoEndpoints.TryOpenResettableSessionAsync). This is about not OFFERING the gesture on
    a surface where it is not the operator's.

    Compared against the tenant the API will reset without credentials, passed to both
    bundles from the same BOOTH_TENANT_ID, so the two cannot drift.
  */
  const isBoothSurface =
    import.meta.env.VITE_RESETTABLE_TENANT !== undefined &&
    import.meta.env.VITE_DEMO_TENANT === import.meta.env.VITE_RESETTABLE_TENANT;

  const header = (
    <header className="quick-entry__header">
      <Logo size={32} onDark />
      {isBoothSurface && <BoothReset onReset={resetForm} />}
    </header>
  );

  if (status === "loading") {
    return (
      <div className="quick-entry quick-entry--centered">
        <StateMessage tone="loading" title="Loading vendors and categories…" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="quick-entry quick-entry--centered">
        <StateMessage
          tone="error"
          title="Can’t reach EasyFarm right now."
          detail={error ?? "Check your connection and try again."}
          action={
            <button className="btn btn--primary" onClick={() => window.location.reload()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  if (submitState === "success" || submitState === "queued") {
    const isQueued = submitState === "queued";
    const attemptedLabel = lastResult?.anomaly
      ? (paymentMethodById.get(lastResult.anomaly.attemptedPaymentMethodId)?.label ??
        "this payment method")
      : null;

    return (
      <div className="quick-entry">
        {header}
        <OutboxStatus items={outboxItems} />
        <div className="quick-entry__success-wrap">
          <div
            className="quick-entry__success"
            role="status"
            data-state={isQueued ? "pending" : "success"}
          >
            <span className="quick-entry__success-icon" aria-hidden="true">
              <Icon name={isQueued ? "wifi-off" : "check"} size={30} strokeWidth={2.4} />
            </span>
            <h1>{isQueued ? "Saved on this phone" : "Entry added"}</h1>
            <p>
              {isQueued
                ? // Not "you’re offline": an entry is queued whenever the request did not
                  // reach us — ApiError.neverReachedTheServer covers 502 and 504 as well as
                  // a dead network — and a 502 leaves the visitor's own connection perfectly
                  // healthy. Measured 2026-09-06: navigator.onLine true while the app blamed
                  // their signal. It is held either way; whose fault it is, we do not know.
                  /*
                    IT NO LONGER REPEATS THE HEADING. This read "Saved on this phone. It'll
                    appear on the laptop dashboard as soon as we can send it." — directly
                    under an <h1> that already says "Saved on this phone", so the visitor read
                    the same four words twice in a row. Found 2026-09-08 by walking the
                    airplane-mode step of the device script and looking at the screen.

                    Three statements of one fact were on that screen at once: the outbox badge
                    at the top ("1 entry pending — will sync automatically as soon as we can
                    send it"), the heading, and this line opening with the heading again.

                    The ONLINE path has always had the right shape — heading "Entry added",
                    body "It just appeared on the laptop dashboard" — a heading that says what
                    happened and a body that says what it means. This now matches it.
                  */
                  "It’ll appear on the laptop dashboard as soon as we can send it."
                : "It just appeared on the laptop dashboard."}
            </p>
            {!isQueued && lastResult?.anomaly && attemptedLabel && (
              <PaymentAnomalyNotice
                variant="banner"
                attemptedLabel={attemptedLabel}
                previouslyUsedLabels={lastResult.anomaly.previouslyUsed.map((m) => m.label)}
              />
            )}
            <button className="btn btn--primary" onClick={resetForm}>
              Add another entry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    /*
      `data-reference-source` is the machine-readable half of the offline fallback
      (rules/ui-breadcrumbs.md §1) — a tester reads declared state instead of
      inferring it from which vendors happen to be on screen. The visible half is
      the note below; the two are set from the same value so they cannot disagree.
    */
    <div className="quick-entry" data-reference-source={fromCache ? "cache" : "live"}>
      {header}
      <OutboxStatus items={outboxItems} />

      {/*
        Said out loud, because the alternative is presenting a saved pick list as a
        live one. It is deliberately a quiet note rather than a warning banner: at
        the booth this state is the airplane-mode beat WORKING, and the thing a
        visitor actually needs to know — that their entry has not reached the farm
        office yet — is the outbox's job, directly above this.
      */}
      {fromCache && (
        <p className="quick-entry__offline-note" role="status">
          Offline — showing your saved vendors and categories. Entries are held until
          you have signal.
        </p>
      )}

      <main className="quick-entry__form">
        {/*
          A visually-hidden h1, and this is the screen that most needed one.
          
          This is the page a stranger lands on from the QR code — the never-cut path —
          and its heading outline started at h2 with four numbered steps ("1. Vendor",
          "2. Category", …) and nothing above them saying what the page was. QuickEntry
          DOES contain an h1, but only in the success state ("Entry added"), so the form a
          visitor actually arrives at had none.
          
          Hidden rather than visible because the design leads with the EasyFarm masthead
          and a visible title above the first step would push the four-tap flow down the
          screen — the one thing the ten-second interaction cannot afford. Same treatment,
          and the same reasoning, as the Dashboard's.
          
          The axe gate does not catch this: page-has-heading-one is MODERATE impact and
          the gate filters to serious/critical.
        */}
        <h1 className="visually-hidden">Add a farm expense</h1>

        {vendors.length === 0 || accountItems.length === 0 ? (
          <StateMessage
            tone="empty"
            title="Nothing to pick from yet."
            detail="Vendors and categories haven't been seeded for this tenant."
          />
        ) : (
          <>
            {/*
              Above the pickers on purpose. Scanning is the beat we want a visitor
              to reach for first; the manual steps below are the fallback, and they
              double as the correction path when the analyzer guesses wrong.
            */}
            <ReceiptScan onAnalyzed={applyScan} entityId={entityId} />

            {/*
              Driven by this screen's OWN `scan` state rather than kept inside ReceiptScan,
              so it inherits the lifecycle that is already correct here: `resetForm` clears
              `scan`, so the notice goes when the form does after a submit. State living in
              the shared component would have survived that reset and left the last scan's
              confidence sitting over a blank form.
            */}
            {/*
              ABOVE the confidence notice, and it does NOT replace it. The two say different
              true things and only one of them is about the model: the category was recalled
              and cannot be wrong, while the amount and date were still read off the photo
              and can be. Suppressing the notice here would quietly extend "cannot be wrong"
              to the figures, which is the over-claim this whole three-beat structure exists
              to avoid.
            */}
            {rememberedVendorName && (
              <p
                className="quick-entry__remembered"
                role="status"
                data-state="remembered"
              >
                {/* Names the vendor AND the category, so a prospect sees a specific decision
                    being recalled rather than a generic reassurance — same wording as the
                    laptop widget, because it is the same claim. */}
                Remembered {rememberedVendorName}
                {rememberedCategoryName
                  ? ` — filed under ${rememberedCategoryName}, the way you mapped it before.`
                  : " — filed the way you mapped it before."}
              </p>
            )}

            {scan?.confidence && (
              <ReceiptConfidenceNotice
                confidence={scan.confidence}
                date={bookingDate}
                /* THIS screen corrects; the dashboard's capture box files immediately and
                   passes no handler, so its notice stays a statement. */
                onDateChange={setDateOverride}
              />
            )}

            {/* ef-011 — the file was kept but deliberately not read (a PDF). Mutually
                exclusive with the notice above by construction: the API returns a null
                confidence in exactly this case. */}
            {scan?.notAnalyzedReason && (
              <p className="quick-entry__status" role="status" data-state="not-analyzed">
                {scan.notAnalyzedReason}
              </p>
            )}

            <section className="quick-entry__section">
              <h2 id="vendor-heading" className="quick-entry__heading">
                1. Vendor
              </h2>
              <PickerGrid
                legendId="vendor-heading"
                items={vendorItems}
                selectedId={vendorId}
                onSelect={setVendorId}
                initialVisibleCount={MOST_USED_COUNT}
                itemNamePlural="vendors"
                itemNameSingular="vendor"
              />
            </section>

            <section className="quick-entry__section">
              <h2 id="category-heading" className="quick-entry__heading">
                2. Category
              </h2>
              <PickerGrid
                legendId="category-heading"
                items={accountItems}
                selectedId={accountId}
                onSelect={setAccountId}
                initialVisibleCount={MOST_USED_COUNT}
                itemNamePlural="categories"
                itemNameSingular="category"
              />
            </section>

            {showPaymentStep && (
              <section className="quick-entry__section">
                <h2 id="payment-heading" className="quick-entry__heading">
                  3. Payment method
                </h2>
                <PickerGrid
                  legendId="payment-heading"
                  items={paymentItems}
                  selectedId={paymentMethodId}
                  onSelect={setPaymentMethodId}
                />
              </section>
            )}

            <section className="quick-entry__section">
              <h2 id="amount-heading" className="quick-entry__heading">
                {amountStepNumber}. Amount
              </h2>
              <AmountKeypad digits={amountDigits} onDigitsChange={setAmountDigits} />
            </section>
          </>
        )}
      </main>

      {(vendors.length > 0 && accountItems.length > 0) && (
        <footer className="quick-entry__footer">
          {submitState === "error" && (
            <p className="quick-entry__error" role="alert">
              {submitError}
            </p>
          )}
          <button
            type="button"
            className="btn btn--primary quick-entry__submit"
            disabled={!canSubmit || submitState === "submitting"}
            onClick={() => void handleSubmit()}
          >
            {submitState === "submitting" ? "Submitting…" : "Submit"}
          </button>
        </footer>
      )}
    </div>
  );
}
